// src/pages/LoginPage.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import logo from "../logos/sp-logo.jpg";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      const role = user?.role || "student";
      navigate(role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(err.message || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  }

  function fillDemo(type) {
    if (type === "student") { setEmail("student@demo.com"); setPassword("demo1234"); }
    else { setEmail("admin@demo.com"); setPassword("admin1234"); }
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--sp-black)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
      position: "relative",
      overflow: "hidden",
      fontFamily: "var(--font-body)"
    }}>
      {/* Background pattern */}
      <div style={{
        position: "absolute", inset: 0, opacity: 0.04,
        backgroundImage: "repeating-linear-gradient(45deg, var(--sp-yellow) 0, var(--sp-yellow) 1px, transparent 0, transparent 50%)",
        backgroundSize: "20px 20px"
      }} />

      {/* Glow */}
      <div style={{
        position: "absolute",
        top: "20%", left: "50%", transform: "translate(-50%, -50%)",
        width: 600, height: 600,
        background: "radial-gradient(circle, rgba(255,214,0,0.08) 0%, transparent 70%)",
        pointerEvents: "none"
      }} />

      <div style={{
        width: "100%", maxWidth: 440,
        animation: "fadeIn 0.5s ease both"
      }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 60, height: 60, background: "var(--sp-yellow)", borderRadius: 16,
            marginBottom: 16, boxShadow: "var(--shadow-yellow)"
          }}>
            <img src={logo} alt="Science Point logo" style={{ width: 40, height: 40, objectFit: "contain" }} />
          </div>
          <h1 style={{
            fontFamily: "var(--font-display)",
            fontSize: 32, color: "var(--sp-white)",
            letterSpacing: "0.06em", marginBottom: 4
          }}>SCIENCE POINT</h1>
          <p style={{ fontSize: 13, color: "var(--sp-gray-mid)", fontWeight: 400 }}>
            Academic &amp; Admission Care
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: "var(--sp-dark-2)",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: var_("--radius-xl"),
          padding: "36px 32px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.5)"
        }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: "var(--sp-white)", marginBottom: 6 }}>
            Welcome back
          </h2>
          <p style={{ fontSize: 14, color: "var(--sp-gray-mid)", marginBottom: 28 }}>
            Sign in to your account to continue learning.
          </p>

          {/* Demo buttons */}
          <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
            <DemoBtn label="Student Demo" onClick={() => fillDemo("student")} />
            <DemoBtn label="Admin Demo" onClick={() => fillDemo("admin")} />
          </div>

          <div style={{
            textAlign: "center", fontSize: 12, color: "var(--sp-gray-mid)",
            marginBottom: 20, display: "flex", alignItems: "center", gap: 10
          }}>
            <hr style={{ flex: 1, border: "none", borderTop: "1px solid rgba(255,255,255,0.07)" }} />
            <span>or enter manually</span>
            <hr style={{ flex: 1, border: "none", borderTop: "1px solid rgba(255,255,255,0.07)" }} />
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--sp-gray-light)", marginBottom: 6 }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                style={inputStyle}
                onFocus={e => { e.target.style.borderColor = "var(--sp-yellow)"; e.target.style.boxShadow = "0 0 0 3px rgba(255,214,0,0.12)"; }}
                onBlur={e => { e.target.style.borderColor = "rgba(255,255,255,0.1)"; e.target.style.boxShadow = "none"; }}
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--sp-gray-light)", marginBottom: 6 }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                style={inputStyle}
                onFocus={e => { e.target.style.borderColor = "var(--sp-yellow)"; e.target.style.boxShadow = "0 0 0 3px rgba(255,214,0,0.12)"; }}
                onBlur={e => { e.target.style.borderColor = "rgba(255,255,255,0.1)"; e.target.style.boxShadow = "none"; }}
              />
            </div>

            {error && (
              <div style={{
                padding: "12px 16px", background: "rgba(220,38,38,0.1)",
                border: "1px solid rgba(220,38,38,0.3)", borderRadius: 8,
                color: "#FC8181", fontSize: 13, marginBottom: 20,
                animation: "scaleIn 0.2s ease"
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%", padding: "13px",
                background: loading ? "var(--sp-gray)" : "var(--sp-yellow)",
                color: "var(--sp-black)", fontWeight: 700, fontSize: 15,
                border: "none", borderRadius: 10,
                transition: "all var(--transition)",
                fontFamily: "var(--font-body)",
                cursor: loading ? "not-allowed" : "pointer",
                letterSpacing: "0.02em"
              }}
              onMouseEnter={e => { if (!loading) { e.target.style.background = "var(--sp-yellow-dark)"; e.target.style.transform = "translateY(-1px)"; e.target.style.boxShadow = "var(--shadow-yellow)"; }}}
              onMouseLeave={e => { e.target.style.background = loading ? "var(--sp-gray)" : "var(--sp-yellow)"; e.target.style.transform = "none"; e.target.style.boxShadow = "none"; }}
            >
              {loading ? "Signing in…" : "Sign In →"}
            </button>
          </form>
        </div>

        {/* Footer */}
        <footer style={{
          marginTop: 28,
          textAlign: "center",
          color: "var(--sp-gray-mid)",
          fontSize: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          alignItems: "center"
        }}>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{
                display: "grid", placeItems: "center",
                width: 22, height: 22, borderRadius: 7,
                background: "rgba(255,214,0,0.15)"
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" fill="var(--sp-yellow)"/>
                </svg>
              </div>
              <span>House # 04, Road # 11, Rupnagar, R/A, Mirpur, Dhaka-1216</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{
                display: "grid", placeItems: "center",
                width: 22, height: 22, borderRadius: 7,
                background: "rgba(255,214,0,0.15)"
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.21c1.21.48 2.53.74 3.88.74a1 1 0 011 1V20a1 1 0 01-1 1C10.07 21 3 13.93 3 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.35.26 2.67.74 3.88a1 1 0 01-.21 1.11l-2.2 2.2z" fill="var(--sp-yellow)"/>
                </svg>
              </div>
              <span>01905468072</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{
                display: "grid", placeItems: "center",
                width: 22, height: 22, borderRadius: 7,
                background: "rgba(255,214,0,0.15)"
              }}>
                <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1h12a1 1 0 011 1v6a1 1 0 01-1 1H1a1 1 0 01-1-1V2a1 1 0 011-1z" stroke="var(--sp-yellow)" strokeWidth="1.5"/>
                  <path d="M1 1l6 5 6-5" stroke="var(--sp-yellow)" strokeWidth="1.5"/>
                </svg>
              </div>
              <span>sciencepoint2k22@gmail.com</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

// Helper to avoid css var in inline style (not allowed in raw strings)
function var_(v) { return `var(${v})`; }

const inputStyle = {
  width: "100%", padding: "11px 14px", fontSize: 14,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 8, color: "var(--sp-white)",
  outline: "none", transition: "all var(--transition)",
  fontFamily: "var(--font-body)"
};

function DemoBtn({ label, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        flex: 1, padding: "9px 12px", fontSize: 12, fontWeight: 600,
        background: hov ? "rgba(255,214,0,0.12)" : "rgba(255,214,0,0.06)",
        color: "var(--sp-yellow)",
        border: "1px solid rgba(255,214,0,0.2)", borderRadius: 8,
        transition: "all 0.15s ease", fontFamily: "var(--font-body)"
      }}
    >
      {label}
    </button>
  );
}
